import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Name from 'components/Form/Name';
import Telephone from 'components/Form/Telephone';
import DateRange from 'components/Form/DateRange';
import Location from 'components/Form/Location';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { setAttribute } from 'components/Form/shared/setAttribute';

import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import { SubstanceDrugsMandatoryTreatmentItemModel } from 'forms/sections/substance/drugs/mandatoryTreatment/types';
import { CheckboxModel } from 'forms/shared/types';
import NotApplicable from 'components/Form/NotApplicable';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Text from 'components/Form/Text';
import TelephoneCollection from 'components/Form/Telephone/TelephoneCollection';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import DrugType from './DrugType';
import DrugTypes from './DrugTypes';

interface Props extends Partial<SubstanceDrugsMandatoryTreatmentItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  formType?: string
}

export default class OrderedTreatment extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,

      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (updateValues: Partial<SubstanceDrugsMandatoryTreatmentItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        OrderedBy: this.props.OrderedBy,
        Explanation: this.props.Explanation,
        ActionTaken: this.props.ActionTaken,
        NoActionTakenExplanation: this.props.NoActionTakenExplanation,
        DrugType: this.props.DrugType,
        TreatmentProvider: this.props.TreatmentProvider,
        TreatmentProviderNotApplicable: this.props.TreatmentProviderNotApplicable,
        TreatmentProviderAddress: this.props.TreatmentProviderAddress,
        TreatmentProviderTelephone: this.props.TreatmentProviderTelephone,
        TreatmentDates: this.props.TreatmentDates,
        TreatmentCompleted: this.props.TreatmentCompleted,
        NoTreatmentExplanation: this.props.NoTreatmentExplanation,
        Comments: this.props.Comments,
        OrderedByComments: this.props.OrderedByComments,
        NoActionTakenComments: this.props.NoActionTakenComments,
        NoTreatmentComments: this.props.NoTreatmentComments,
        DueToDrugs: this.props.DueToDrugs,
        DrugTypeOther: this.props.DrugTypeOther,
        DrugTypes: this.props.DrugTypes,
        DueToPrescription: this.props.DueToPrescription,
        PrescriptionDrug: this.props.PrescriptionDrug,
        TreatmentProviderInPractice: this.props.TreatmentProviderInPractice,
        TreatmentProviderPracticeName: this.props.TreatmentProviderPracticeName,
        TreatmentProviderPracticeNameNotApplicable: this.props.TreatmentProviderPracticeNameNotApplicable,
        TreatmentProviderTelephones: this.props.TreatmentProviderTelephones,
        TreatmentProviderEmails: this.props.TreatmentProviderEmails,
        TreatmentStatus: this.props.TreatmentStatus,
        OrderedBySingle: this.props.OrderedBySingle,
        OrderedBySingleOther: this.props.OrderedBySingleOther,
        ...updateValues,
      });
    }
  };

  /**
   * This is the list of people that ordered counseling or treatment
   */
  getPeopleWhoOrderedCounseling = (cb: CheckboxModel) => {
    const selected = cb.value;
    let list = [...((this.props.OrderedBy || {}).values || [])];

    if (list.includes(selected)) {
      list.splice(list.indexOf(selected), 1);
    } else if (selected === 'None' || list.includes('None')) {
      list = [selected];
    } else {
      list.push(selected);
    }
    return list;
  };

  updateOrderedBy = (cb: CheckboxModel) => {
    this.update({
      OrderedBy: {
        values: this.getPeopleWhoOrderedCounseling(cb),
      },
    });
  };

  updateExplanation = (values: SubstanceDrugsMandatoryTreatmentItemModel['Explanation']) => {
    this.update({ Explanation: values });
  };

  updateActionTaken = (values: SubstanceDrugsMandatoryTreatmentItemModel['ActionTaken']) => {
    let additionalValues = {};
    if (values.value === 'Yes') {
      additionalValues = {
        NoActionTakenExplanation: {},
        NoActionTakenComments: {},
      };
    } else if (values.value === 'No') {
      additionalValues = {
        DrugType: {},
        TreatmentProvider: {},
        TreatmentProviderAddress: {},
        TreatmentProviderTelephone: {},
        TreatmentDates: {},
        Comments: {},
        TreatmentCompleted: {},
        NoTreatmentExplanation: {},
        NoTreatmentComments: {},
      };
    }
    this.update({ ActionTaken: values, ...additionalValues });
  };

  updateNoActionTakenExplanation = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['NoActionTakenExplanation']
  ) => {
    this.update({ NoActionTakenExplanation: values });
  };

  updateDrugType = (values: SubstanceDrugsMandatoryTreatmentItemModel['DrugType']) => {
    this.update({ DrugType: values });
  };

  updateTreatmentProvider = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProvider']
  ) => {
    this.update({ TreatmentProvider: values });
  };

  updateTreatmentProviderNotApplicable = (treatmentProviderNotApplicable: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderNotApplicable']) => {
    // Delete the name if it is not applicable because the user selected "I Don't Know"
    const treatmentProvider: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProvider'] | undefined = treatmentProviderNotApplicable?.applicable ? this.props.TreatmentProvider : {} as SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProvider'];

    this.update({
      TreatmentProvider: treatmentProvider,
      TreatmentProviderNotApplicable: treatmentProviderNotApplicable,
    });
  };

  updateTreatmentProviderAddress = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderAddress']
  ) => {
    this.update({ TreatmentProviderAddress: values });
  };

  updateTreatmentProviderTelephone = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderTelephone']
  ) => {
    this.update({ TreatmentProviderTelephone: values });
  };

  updateTreatmentDates = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentDates']) => {
    this.update({ TreatmentDates: values });
  };

  updateTreatmentCompleted = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentCompleted']
  ) => {
    this.update({ TreatmentCompleted: values });
  };

  updateNoTreatmentExplanation = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['NoTreatmentExplanation']
  ) => {
    this.update({ NoTreatmentExplanation: values });
  };

  updateComments = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['Comments']
  ) => {
    this.update({ Comments: values });
  };

  updateOrderedByComments = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['OrderedByComments']
  ) => {
    this.update({ OrderedByComments: values });
  };

  updateNoActionTakenComments = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['NoActionTakenComments']
  ) => {
    this.update({ NoActionTakenComments: values });
  };

  updateNoTreatmentComments = (
    values: SubstanceDrugsMandatoryTreatmentItemModel['NoTreatmentComments']
  ) => {
    this.update({ NoTreatmentComments: values });
  };

  updateDueToDrugs = (values: SubstanceDrugsMandatoryTreatmentItemModel['DueToDrugs']) => {
    this.update({ DueToDrugs: values });
  };

  updateDrugTypes = (values: SubstanceDrugsMandatoryTreatmentItemModel['DrugTypes']) => {
    this.update({ DrugTypes: values });
  };

  updateDrugTypeOther = (values: SubstanceDrugsMandatoryTreatmentItemModel['DrugTypeOther']) => {
    this.update({ DrugTypeOther: values });
  };

  updateDueToPrescription = (values: SubstanceDrugsMandatoryTreatmentItemModel['DueToPrescription']) => {
    this.update({ DueToPrescription: values });
  };

  updatePrescriptionDrug = (values: SubstanceDrugsMandatoryTreatmentItemModel['PrescriptionDrug']) => {
    this.update({ PrescriptionDrug: values });
  };

  updateTreatmentProviderInPractice = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderInPractice']) => {
    this.update({ TreatmentProviderInPractice: values });
  };

  updateTreatmentProviderTelephones = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderTelephones']) => {
    this.update({ TreatmentProviderTelephones: values });
  };

  updateTreatmentProviderEmails = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderEmails']) => {
    this.update({ TreatmentProviderEmails: values });
  };

  updateTreatmentStatus = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentStatus']) => {
    this.update({ TreatmentStatus: values });
  };

  updateOrderedBySingle = (values: SubstanceDrugsMandatoryTreatmentItemModel['OrderedBySingle']) => {
    this.update({ OrderedBySingle: values });
  };

  updateOrderedBySingleOther = (values: SubstanceDrugsMandatoryTreatmentItemModel['OrderedBySingleOther']) => {
    this.update({ OrderedBySingleOther: values });
  };

  updateTreatmentProviderPracticeName = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderPracticeName']) => {
    this.update({ TreatmentProviderPracticeName: values });
  };

  updateTreatmentProviderPracticeNameNotApplicable = (values: SubstanceDrugsMandatoryTreatmentItemModel['TreatmentProviderPracticeNameNotApplicable']) => {
    this.update({ TreatmentProviderPracticeNameNotApplicable: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    /* PVQ Updates to SubstanceDrugsMandatoryTreatmentItemModel
  DueToDrugs?: YesOrNoModel
  DrugTypeOther?: FieldModel
  DrugTypes?: CheckboxGroupModel
  DueToPrescription?: YesOrNoModel
  PrescriptionDrug?: FieldModel
   TreatmentProviderInPractice?: YesOrNoModel
  TreatmentProviderTelephones?: BranchCollectionModel<TelephoneItemModel>
  TreatmentProviderEmails?: BranchCollectionModel<EmailItemModel>
  TreatmentStatus?: FieldModel
  OrderedBySingle?: RadioModel
  OrderedBySingleOther?: FieldModel
*/
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-ordered-treatment">
        <Field
          title={i18n.t('substance.pvq.drugs.ordered.heading.orderedBy')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderedBySingle`}
        >
          <RadioGroup
            className="ordered-by-single option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={(this.props.OrderedBySingle || {}).value}
            attribute={`${attribute}.OrderedBySingle`}
          >
            <Radio
              name="Employer"
              label={i18n.t('substance.pvq.drugs.ordered.orderedBy.label.employer')}
              value="Employer"
              className="employer"
              onUpdate={this.updateOrderedBySingle}
              onError={this.props.onError}
            />
            <Radio
              name="MilitaryCommander"
              label={i18n.t('substance.pvq.drugs.ordered.orderedBy.label.militaryCommander')}
              value="MilitaryCommander"
              className="medical-professional"
              onUpdate={this.updateOrderedBySingle}
              onError={this.props.onError}
            />
            <Radio
              name="MedicalProfessional"
              label={i18n.t('substance.pvq.drugs.ordered.orderedBy.label.medicalProfessional')}
              value="MedicalProfessional"
              className="medical-professional"
              onUpdate={this.updateOrderedBySingle}
              onError={this.props.onError}
            />
            <Radio
              name="MentalHealthProfessional"
              label={i18n.t('substance.pvq.drugs.ordered.orderedBy.label.mentalHealthProfessional')}
              value="MentalHealthProfessional"
              className="mental-health-professional"
              onUpdate={this.updateOrderedBySingle}
              onError={this.props.onError}
            />
            <Radio
              name="Judge"
              label={i18n.t('substance.pvq.drugs.ordered.orderedBy.label.judge')}
              value="Judge"
              className="judge"
              onUpdate={this.updateOrderedBySingle}
              onError={this.props.onError}
            />
            <Radio
              name="Other"
              label="Other"
              value="Other"
              className="other"
              onUpdate={this.updateOrderedBySingle}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>
        {this.props.OrderedBySingle?.value === 'Other' && (
          <Field
            title={i18n.t('substance.pvq.drugs.ordered.heading.explanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.OrderedBySingleOther`}
          >
            <Textarea
              {...this.props.OrderedBySingleOther}
              name="OrderedBySingleOther"
              className="orderedBySingleOther"
              onUpdate={this.updateOrderedBySingleOther}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.OrderedBySingleOther`}
            />
          </Field>
        )}

        <Branch
          {...this.props.DueToPrescription}
          name="DueToPrescription"
          label={i18n.t('substance.pvq.drugs.ordered.heading.dueToPrescription')}
          labelSize="h4"
          className="dueToPrescription"

          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateDueToPrescription}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DueToPrescription`}
          warning
        />
        {this.props.DueToPrescription?.value === 'Yes' && (
        <Field
          title={i18n.t('substance.pvq.drugs.ordered.heading.prescriptionDrug')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PrescriptionDrug`}
        >
          <Text
            {...this.props.PrescriptionDrug}
            name="PrescriptionDrug"
            className="prescriptionDrug"
            onUpdate={this.updatePrescriptionDrug}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.PrescriptionDrug`}
          />
        </Field>
        )}
        <Branch
          {...this.props.DueToDrugs}
          name="DueToDrugs"
          label={i18n.t('substance.pvq.drugs.ordered.heading.dueToDrugs')}
          labelSize="h4"
          className="dueToDrugs"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateDueToDrugs}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DueToDrugs`}
          warning
        />
        {this.props.DueToDrugs?.value === 'Yes' && (
        <>
          <Field
            title={i18n.t('substance.pvq.drugs.ordered.heading.drugTypes')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.DrugTypes`}
          >
            <DrugTypes
              {...this.props.DrugTypes}
              onUpdate={this.updateDrugTypes}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.DrugTypes`}
            />
          </Field>

          {this.props.DrugTypes?.values?.includes('Other') && (
          <Field
            title={i18n.t('substance.pvq.drugs.use.heading.otherDrugType')}
            attribute={`${attribute}.DrugTypeOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.DrugTypeOther}
              name="DrugTypeOther"
              attribute={`${attribute}.DrugTypeOther`}
              className="drugTypeOther"
              onUpdate={this.updateDrugTypeOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          )}
        </>
        )}
        <Branch
          name="ActionTaken"
          label={i18n.t('substance.pvq.drugs.ordered.heading.actionTaken')}
          labelSize="h4"
          className="action-taken"
          {...this.props.ActionTaken}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateActionTaken}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ActionTaken`}
          warning
        />

        {this.props.ActionTaken?.value === 'No' && (
        <Field
          title={i18n.t('substance.pvq.drugs.ordered.heading.noActionTakenExplanation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NoActionTakenExplanation`}
        >
          <Textarea
            name="NoActionTakenExplanation"
            className="no-action-taken-explanation"
            {...this.props.NoActionTakenExplanation}
            onUpdate={this.updateNoActionTakenExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.NoActionTakenExplanation`}
          />
        </Field>
        )}
        {this.props.ActionTaken?.value === 'Yes' && (

        <>
          <Field
            title={i18n.t('substance.pvq.drugs.ordered.heading.treatmentDates')}
            adjustFor="daterange"
            help="substance.drugs.ordered.help.treatmentDates"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TreatmentDates`}
          >
            <DateRange
              name="TreatmentDates"
              className="treatment-dates"
              {...this.props.TreatmentDates}
              minDateEqualTo
              onUpdate={this.updateTreatmentDates}
              onError={this.props.onError}
              required={this.props.required}
              hideFromDay
              hideToDay
              attribute={`${attribute}.TreatmentDates`}
            />
          </Field>
          <Field
            title={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProvider')}
            filterErrors={Name.requiredErrorsOnly}
            scrollIntoView={this.props.scrollIntoView}
          >
            <NotApplicable
                // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.TreatmentProviderNotApplicable}
              attribute={setAttribute(this.props.attribute, 'TreatmentProviderNotApplicable')}
              className="treatment-provider-not-applicable"
              includeNAConfirmation
              label={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProvideridk')}
              name="TreatmentProviderNotApplicable"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateTreatmentProviderNotApplicable}
              or={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProvideror')}
              required={this.props.required}
            >
              <PVQName
                {...this.props.TreatmentProvider}
                aria-label="Provide the name of the treatment provider"
                attribute={`${attribute}.TreatmentProvider`}
                className="treatment-provider"
                exactAttribute
                hideMiddleName
                showLastFirstMiddleNameOrder
                name="TreatmentProvider"
                onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
                onUpdate={this.updateTreatmentProvider}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />
            </NotApplicable>
          </Field>
          <Branch
            {...this.props.TreatmentProviderInPractice}
            name="TreatmentProviderInPractice"
            label={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProviderInPractice')}
            labelSize="h4"
            className="action-taken"
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateTreatmentProviderInPractice}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TreatmentProviderInPractice`}
            showDNK
            warning
          />
          <Field
            title={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProviderPracticeName')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TreatmentProviderPracticeName`}
          >
            <Text
              {...this.props.TreatmentProviderPracticeName}
              name="treatmentProviderPracticeName"
              className="treatmentProviderPracticeName"
              onUpdate={this.updateTreatmentProviderPracticeName}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.TreatmentProviderPracticeName`}
            />
          </Field>
          <TelephoneCollection
            {...this.props.TreatmentProviderTelephones}
            onUpdate={this.updateTreatmentProviderTelephones}
            onError={this.props.onError}
            required={this.props.required}
            showTimeOfDay
            hideInitialBranchQuestion
            showDNK
            allowNotApplicable
            showNumberType={false}
            attribute={`${attribute}.TreatmentProviderTelephones`}
            telephoneFieldTitle={i18n.t('substance.pvq.drugs.ordered.heading.phoneNumbers.title')}
            appendFieldTitle={i18n.t('substance.pvq.drugs.ordered.heading.phoneNumbers.append')}
            branchFieldTitle={i18n.t('substance.pvq.drugs.ordered.heading.phoneNumbers.branch.title')}
          />
          <EmailCollection
            {...this.props.TreatmentProviderEmails}
            onUpdate={this.updateTreatmentProviderEmails}
            onError={this.props.onError}
            required={this.props.required}
            showEmailUnknown
            includeEmailType={false}
            attribute={`${attribute}.TreatmentProviderEmails`}
            emailFieldTitle={i18n.t('substance.pvq.drugs.ordered.heading.emails.title')}
            appendFieldTitle={i18n.t('substance.pvq.drugs.ordered.heading.emails.append')}
            branchFieldTitle={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProviderPhoneNumbers')}
          />
          <Field
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="address"
            attribute={`${attribute}.TreatmentProviderAddress`}
            title={i18n.t('substance.pvq.drugs.ordered.heading.treatmentProviderAddress')}
          >

            <PVQLocation
              {...this.props.TreatmentProviderAddress}
              addressBook="Provider"
              addressBooks={this.props.addressBooks}
              attribute={`${attribute}.TreatmentProviderAddress`}
              className="treatment-provider-address"
              dispatch={this.props.dispatch}
              domesticTitle={i18n.t(`substance.pvq.drugs.ordered.heading.treatmentProviderAddressDomesticTitle`)}
              foreignTitle={i18n.t(`substance.pvq.drugs.ordered.heading.treatmentProviderAddressForeignTitle`)}
              geocode
              includeMilitaryStates
              layout={PVQLocation.PVQ_DOMESTIC_FOREIGN_MILITARY_DIPLOMATIC_LOCATION}
              isPoBoxAllowed={false}
              name="TreatmentProviderAddress"
              onError={this.props.onError}
              onUpdate={this.updateTreatmentProviderAddress}
              required={this.props.required}
              showInternationalStreet={false}
              showMilitaryInstallation
            />
          </Field>

          <Branch
            name="TreatmentCompleted"
            label={i18n.t('substance.pvq.drugs.ordered.heading.treatmentCompleted')}
            labelSize="h4"
            className="treatment-completed no-margin-bottom"
            {...this.props.TreatmentCompleted}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateTreatmentCompleted}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.TreatmentCompleted`}
          />

          {this.props.TreatmentCompleted?.value === 'No' && (
            <>
              <Field
                title={i18n.t('substance.pvq.drugs.ordered.heading.noTreatmentExplanation')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.NoTreatmentExplanation`}
              >
                <Textarea
                  {...this.props.NoTreatmentExplanation}
                  name="NoTreatmentExplanation"
                  className="noTreatmentExplanation"
                  onUpdate={this.updateNoTreatmentExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.NoTreatmentExplanation`}
                />
              </Field>
              <Field
                title={i18n.t('substance.pvq.drugs.ordered.heading.treatmentStatus')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.TreatmentStatus`}
              >
                <Textarea
                  {...this.props.TreatmentStatus}
                  name="TreatmentStatus"
                  className="treatmentStatus"
                  onUpdate={this.updateTreatmentStatus}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.TreatmentStatus`}
                />
              </Field>
            </>
          )}
        </>
        )}
      </div>
    ) : (
      <div className="drug-ordered-treatment">
        <Field
          title={i18n.t('substance.drugs.ordered.heading.orderedBy')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderedBy`}
        >
          {i18n.m('substance.drugs.ordered.para.orderedBy')}
          <CheckboxGroup
            className="ordered-by option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValues={(this.props.OrderedBy || {}).values}
            attribute={`${attribute}.OrderedBy`}
          >
            <Checkbox
              name="Employer"
              label={i18n.t('substance.drugs.ordered.orderedBy.label.employer')}
              value="Employer"
              className="employer"
              onUpdate={this.updateOrderedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="MedicalProfessional"
              label={i18n.t('substance.drugs.ordered.orderedBy.label.medicalProfessional')}
              value="MedicalProfessional"
              className="medical-professional"
              onUpdate={this.updateOrderedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="MentalHealthProfessional"
              label={i18n.t('substance.drugs.ordered.orderedBy.label.mentalHealthProfessional')}
              value="MentalHealthProfessional"
              className="mental-health-professional"
              onUpdate={this.updateOrderedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="Judge"
              label={i18n.t('substance.drugs.ordered.orderedBy.label.judge')}
              value="Judge"
              className="judge"
              onUpdate={this.updateOrderedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="None"
              label={i18n.t('substance.drugs.ordered.orderedBy.label.none')}
              value="None"
              className="none"
              onUpdate={this.updateOrderedBy}
              onError={this.props.onError}
            />
          </CheckboxGroup>
        </Field>
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="OrderedByComments"
          className="optional-comment"
          attribute="OrderedByComments"
          {...this.props.OrderedByComments}
          onUpdate={this.updateOrderedByComments}
          required={false}
        />
        <Field
          title={i18n.t('substance.drugs.ordered.heading.explanation')}
          scrollIntoView={this.props.Explanation}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            name="Explanation"
            className="explanation"
            {...this.props.Explanation}
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>

        <Branch
          name="ActionTaken"
          label={i18n.t('substance.drugs.ordered.heading.actionTaken')}
          labelSize="h4"
          className="action-taken"
          {...this.props.ActionTaken}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateActionTaken}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ActionTaken`}
          warning
        />

        <Show when={this.props.ActionTaken?.value === 'No'}>
          <Field
            title={i18n.t('substance.drugs.ordered.heading.noActionTakenExplanation')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.NoActionTakenExplanation`}
          >
            <Textarea
              name="NoActionTakenExplanation"
              className="no-action-taken-explanation"
              {...this.props.NoActionTakenExplanation}
              onUpdate={this.updateNoActionTakenExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.NoActionTakenExplanation`}
            />
          </Field>
          <Comments
            title={i18n.t('substance.comments.optional')}
            name="NoActionTakenComments"
            className="optional-comment"
            attribute="NoActionTakenComments"
            {...this.props.NoActionTakenComments}
            onUpdate={this.updateNoActionTakenComments}
            required={false}
          />
        </Show>

        <Show when={this.props.ActionTaken?.value === 'Yes'}>
          <div>
            <Field
              title={i18n.t('substance.drugs.ordered.heading.drugType')}
              className="drug-type-ordered"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DrugType`}
            >
              <DrugType
                name="DrugType"
                {...this.props.DrugType}
                onUpdate={this.updateDrugType}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute={attribute}
              />
            </Field>

            <Field
              title={i18n.t('substance.drugs.ordered.heading.treatmentProvider')}
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Name
                name="TreatmentProvider"
                prefix="name"
                className="treatment-provider"
                aria-label="Provide the name of the treatment provider"
                {...this.props.TreatmentProvider}
                scrollIntoView={this.props.scrollIntoView}
                hideMiddleName
                onUpdate={this.updateTreatmentProvider}
                onError={this.handleNameError}
                required={this.props.required}
                attribute={`${attribute}.TreatmentProvider`}
              />
            </Field>

            <Field
              title={i18n.t('substance.drugs.ordered.heading.treatmentProviderAddress')}
              optional
              adjustFor="address"
              help="substance.drugs.ordered.help.treatmentProviderAddress"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.TreatmentProviderAddress`}
            >
              <Location
                name="TreatmentProviderAddress"
                className="treatment-provider-address"
                {...this.props.TreatmentProviderAddress}
                label={i18n.t('reference.label.address')}
                layout={Location.ADDRESS}
                geocode
                addressBooks={this.props.addressBooks}
                addressBook="Provider"
                showPostOffice
                dispatch={this.props.dispatch}
                onUpdate={this.updateTreatmentProviderAddress}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.TreatmentProviderAddress`}
              />
            </Field>

            <Field
              title={i18n.t('substance.drugs.ordered.heading.treatmentProviderTelephone')}
              className="treatment-provider-telephone override-required"
              help="substance.drugs.ordered.help.treatmentProviderTelephone"
              adjustFor="telephone"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.TreatmentProviderTelephone`}
            >
              <Telephone
                name="TreatmentProviderTelephone"
                {...this.props.TreatmentProviderTelephone}
                allowNotApplicable={false}
                onUpdate={this.updateTreatmentProviderTelephone}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.TreatmentProviderTelephone`}
              />
            </Field>

            <Field
              title={i18n.t('substance.drugs.ordered.heading.treatmentDates')}
              adjustFor="daterange"
              help="substance.drugs.ordered.help.treatmentDates"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.TreatmentDates`}
            >
              <DateRange
                name="TreatmentDates"
                className="treatment-dates"
                {...this.props.TreatmentDates}
                minDateEqualTo
                onUpdate={this.updateTreatmentDates}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
                attribute={`${attribute}.TreatmentDates`}
              />
            </Field>
            <Comments
              title={i18n.t('substance.comments.optional')}
              name="Comments"
              className="optional-comment"
              attribute="Comments"
              {...this.props.Comments}
              onUpdate={this.updateComments}
              required={false}
            />
            <Branch
              name="TreatmentCompleted"
              label={i18n.t('substance.drugs.ordered.heading.treatmentCompleted')}
              labelSize="h4"
              className="treatment-completed no-margin-bottom"
              {...this.props.TreatmentCompleted}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateTreatmentCompleted}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.TreatmentCompleted`}
            />

            <Show when={this.props.TreatmentCompleted?.value === 'No'}>
              <Field
                title={i18n.t('substance.drugs.ordered.heading.noTreatmentExplanation')}
                titleSize="label"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.NoTreatmentExplanation`}
              >
                <Textarea
                  name="NoTreatmentExplanation"
                  className="no-treatment-explanation"
                  {...this.props.NoTreatmentExplanation}
                  onUpdate={this.updateNoTreatmentExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.NoTreatmentExplanation`}
                />
              </Field>
              <Comments
                title={i18n.t('substance.comments.optional')}
                name="NoTreatmentComments"
                className="optional-comment"
                attribute="NoTreatmentComments"
                {...this.props.NoTreatmentComments}
                onUpdate={this.updateNoTreatmentComments}
                required={false}
              />
            </Show>
          </div>
        </Show>
      </div>
    );
  }
}

OrderedTreatment.defaultProps = {
  ActionTaken: {},
  TreatmentCompleted: {},
  OrderedBy: { values: [] },
  addressBooks: {},
  Comments: {},
  OrderedByComments: {},
  NoActionTakenComments: {},
  NoTreatmentComments: {},
  DueToDrugs: {},
  DrugTypes: {},
  DrugTypeOther: {},
  DueToPrescription: {},
  PrescriptionDrug: {},
  TreatmentProvider: {},
  TreatmentProviderPracticeName: {},
  TreatmentProviderPracticeNameNotApplicable: { $index: undefined, applicable: true },
  TreatmentProviderNotApplicable: { $index: undefined, applicable: true },
  TreatmentProviderInPractice: {},
  TreatmentProviderTelephones: {},
  TreatmentProviderEmails: {},
  TreatmentStatus: {},
  OrderedBySingle: {},
  OrderedBySingleOther: {},
  dispatch: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
